import { createItem, deleteItem, getItem, listItems, updateItem } from './sharepoint';

const LIST = 'agreements';

// Coverage rows ([{ plant, hourlyRateUsd, coverage }]) and support bullets
// ([string, ...]) are stored inline as JSON to keep the list schema flat
// and the index sortable. Defaults mirror the Proxemics "Proposal for
// Liaison Coverage" template (verbatim) so a Director can generate a
// proposal with only the customer + plant info filled in.
export const DEFAULT_SUPPORT_BULLETS = [
  'Monitor parts and production activity inside the plant',
  'Communicate issues directly to the customer',
  'Coordinate with OEM personnel as needed',
  'Support root cause analysis, containment, or rework when required',
];

export const DEFAULT_PROPOSAL_TITLE = 'Proposal for Liaison Coverage';

// Proposal lifecycle. A proposal is a Draft until the Director hits "Send /
// Mark Proposal Out", which writes Proposal-Out coverage onto the supplier
// and flips the agreement to PROPOSAL_OUT. Won/Lost are set from the quick
// actions on the Sales list. These live as the plain `ProposalStatus` column
// (auto-dropped by the SharePoint layer if the column isn't provisioned yet,
// so saves never break — provision it to make the status persist/filter).
export const PROPOSAL_STATUS = {
  DRAFT: 'Draft',
  PROPOSAL_OUT: 'Proposal Out',
  WON: 'Won',
  LOST: 'Lost',
};

// Coverage-line fields the proposal stores INSIDE CoverageRowsJson (already a
// provisioned JSON column, so these persist without any schema change):
//   plant          — display label for the PDF (e.g. "Sterling Heights (SHAP)")
//   plantCode      — the real code used to attach to the supplier assignment
//   hourlyRateUsd  — proposed rate (number)
//   hoursAllocated — proposed hours (number)
//   hoursUnit      — e.g. "Per Production Day"
//   coverage       — free-text rendered on the PDF (auto-composed from
//                    hoursAllocated + hoursUnit, still editable)

// Static second paragraph on the proposal — verbatim from the printed
// template. The first paragraph is auto-composed by proposalPdf.js from
// the customer + plant + requestor fields so the Director doesn't have
// to retype the same sentence shape for every new proposal.
export const DEFAULT_INTRO =
  'Proxemics Consulting provides embedded liaison support inside OEM ' +
  'plants to monitor parts, communicate issues, and assist with ' +
  'resolution directly on the plant floor.';

function decode(row) {
  if (!row) return row;
  let supportBullets = [];
  if (row.SupportBulletsJson) {
    try {
      supportBullets = JSON.parse(row.SupportBulletsJson);
    } catch {
      supportBullets = [];
    }
  }
  let coverageRows = [];
  if (row.CoverageRowsJson) {
    try {
      coverageRows = JSON.parse(row.CoverageRowsJson);
    } catch {
      coverageRows = [];
    }
  }
  return { ...row, supportBullets, coverageRows };
}

function encode(model) {
  const {
    supportBullets = [],
    coverageRows = [],
    ...rest
  } = model;
  return {
    ...rest,
    SupportBulletsJson: JSON.stringify(supportBullets),
    CoverageRowsJson: JSON.stringify(coverageRows),
  };
}

export async function listAgreements(opts) {
  // Newest first, by SharePoint's creation timestamp. Previously this
  // sorted by AgreementNumber desc — we removed agreement numbers in
  // favor of date-based ordering, which matches the rest of the portal
  // (Requests, Parts, Sales Resources all sort by Created desc).
  const rows = await listItems(LIST, {
    orderby: 'fields/Created desc',
    ...opts,
  });
  return rows.map(decode);
}

export async function getAgreement(id) {
  return decode(await getItem(LIST, id));
}

export async function createAgreement(model) {
  // Title is used by the SharePoint UI as the list's display column —
  // derive a human-readable label ("ABC Climate Control — 2026-04-18")
  // so the native SharePoint list view is scannable at a glance. Falls
  // back to any Title the caller supplied, or a generic "Proposal"
  // placeholder so the required column is never blank.
  const company = (model.CustomerCompany || '').trim();
  const date = (model.ProposalDate || '').slice(0, 10);
  const derived = [company, date].filter(Boolean).join(' — ');
  const payload = {
    Title: derived || model.Title || 'Proposal',
    ...model,
  };
  return decode(await createItem(LIST, encode(payload)));
}

export async function updateAgreement(id, model) {
  return decode(await updateItem(LIST, id, encode(model)));
}

export async function deleteAgreement(id) {
  return deleteItem(LIST, id);
}
